import React, { useState, useEffect, useRef } from 'react';
import './Activities.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

// --- Data ---

const ACTIVITIES = [
  {
    id: 'computer-training',
    title: 'Computer Training for Visually Challenged',
    icon: '💻',
    color: '#E3A857',
    description: 'Empowering students with assistive technology and computer skills for independent learning and future careers.',
    features: [
      {
        name: 'JAWS',
        description: 'Job Access With Speech (JAWS) is a screen reading software that converts the computer into a talking computer, enabling blind persons to work independently on Windows, Microsoft Office, programming languages, and the internet.',
        icon: '🔊'
      },
      {
        name: 'Kurzweil',
        description: 'Kurzweil software reads printed text by scanning pages and using OCR (Optical Character Recognition) to convert images to speech, allowing visually challenged individuals to listen to any printed material.',
        icon: '📖'
      },
      {
        name: 'MAGIC',
        description: 'MAGIC magnifies text for low-vision users with screen reader options, enhancing learning by allowing users to see enlarged text and hear it simultaneously.',
        icon: '🔍'
      },
      {
        name: 'Prisma Magnifier',
        description: 'A teaching aid that magnifies text and diagrams on computer or TV monitors, adjustable to the comfort level of children with low vision.',
        icon: '🖥️'
      },
      {
        name: 'Mountbatten Electronic Braille Writer',
        description: 'This innovative device allows students to type in Braille and instantly transcribe to normal text and vice versa, enabling independent examination writing without scribes.',
        icon: '📝'
      }
    ]
  },
  {
    id: 'science-lab',
    title: 'Science Lab & Tactile Learning',
    icon: '🔬',
    color: '#2A3557',
    description: 'Innovative tactile models and assistive devices that make complex scientific concepts accessible to visually challenged students.',
    features: [
      {
        name: 'Tactile Models',
        description: 'Over 50 ingeniously designed tactile models by Biology teacher Mr. Egbert Dawson help students understand intricate scientific structures, enabling scores above 95% in science.',
        icon: '🧬'
      },
      {
        name: 'Award-Winning Exhibit',
        description: 'Our "Fractional Distillation of Petroleum" model won a silver medal at the 93rd National Science Congress, showcasing our innovative approach to science education.',
        icon: '🏅'
      }
    ]
  },
  {
    id: 'braille-printing',
    title: 'Devnar Braille Printing Press',
    icon: '🖨️',
    color: '#C75C4D',
    description: 'One of India\'s finest Braille printing presses, recognized as the "Best Braille Printing Press" by the Vice President of India in 2019.',
    features: [
      {
        name: 'Braille Textbooks',
        description: 'Prints State syllabus textbooks in Braille for visually challenged students across Andhra Pradesh and Telangana.',
        icon: '📚'
      },
      {
        name: 'LIMCA Book of Records',
        description: 'Devnar earned a place in the LIMCA BOOK OF WORLD RECORDS for printing the Bhagavad Gita in Braille.',
        icon: '🏆'
      },
      {
        name: 'Dictionaries & Literature',
        description: 'Prints dictionaries, grammar books, and stories in English, Hindi, and Telugu, supplied to schools across the region.',
        icon: '📘'
      }
    ]
  },
  {
    id: 'library',
    title: 'Digital & Braille Library',
    icon: '📚',
    color: '#E3A857',
    description: 'A comprehensive library with over 1000 Braille books and a digital collection of educational CDs and audio resources.',
    features: [
      {
        name: 'Braille Collection',
        description: 'One of the best school libraries in India with over 1000 Braille books, regularly updated with new additions.',
        icon: '📕'
      },
      {
        name: 'Digital Library',
        description: 'Compact discs of SSC syllabus materials, general knowledge, and popular stories in audio format, accessible with CD players.',
        icon: '💿'
      },
      {
        name: 'Reading Period',
        description: 'Students enjoy dedicated library periods and free time to explore books, enriching their knowledge and imagination.',
        icon: '📖'
      }
    ]
  },
  {
    id: 'vocational-training',
    title: 'Ramananda Vocational Training Centre',
    icon: '🛠️',
    color: '#2A3557',
    description: 'Empowering adult blind individuals with income-generating skills for rehabilitation and sustainable livelihoods.',
    features: [
      {
        name: 'Income-Generating Skills',
        description: 'Training in leaf-plate making, candle making, weaving towels and napkins, detergent and phenyl production, envelope and office file making, and disposable paper-cup production.',
        icon: '💪'
      },
      {
        name: 'Scented Candles & Diyas',
        description: 'Beautiful scented candles and diyas made from superior quality wax, available for sale during festival seasons to support rehabilitation efforts.',
        icon: '🕯️'
      },
      {
        name: 'Computer Education',
        description: 'Advanced computer training to open doors to modern employment opportunities for adult blind individuals.',
        icon: '💻'
      }
    ]
  }
];

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main Activities Component ---

export default function Activities() {
  const [activeTab, setActiveTab] = useState(ACTIVITIES[0].id);

  return (
    <>
      <Header />
      <div className="activities-page devnar">
        <a className="skip-link" href="#main-activities">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-activities">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-activities">
            <Reveal>
              <span className="hero-badge">Our Programs</span>
              <h1>Activities at Devnar</h1>
              <p className="hero-subtitle">
                From assistive technology and science education to vocational training and Braille printing — 
                we create opportunities that transform lives.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-activities" className="wrap page-content-activities">

          {/* --- Intro Section --- */}
          <section className="activities-intro">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">What We Do</span>
              <h2>Beyond Academics</h2>
              <div className="rule" />
              <p className="section-subtitle">
                Devnar Foundation offers a comprehensive range of activities that go beyond traditional education — 
                equipping visually challenged students with skills, confidence, and independence.
              </p>
            </Reveal>
          </section>

          {/* --- Activity Cards (Grid View) --- */}
          <section className="activities-grid-section">
            <div className="activity-tabs">
              {ACTIVITIES.map((activity) => (
                <button
                  key={activity.id}
                  className={`tab-btn ${activeTab === activity.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(activity.id)}
                  style={{ 
                    '--tab-color': activity.color,
                    borderBottomColor: activeTab === activity.id ? activity.color : 'transparent'
                  }}
                >
                  <span className="tab-icon">{activity.icon}</span>
                  <span className="tab-label">{activity.title}</span>
                </button>
              ))}
            </div>

            <div className="activity-content">
              {ACTIVITIES.map((activity) => (
                <div 
                  key={activity.id} 
                  className={`activity-panel ${activeTab === activity.id ? 'active' : ''}`}
                >
                  <Reveal className="activity-header" as="div">
                    <div className="activity-icon-large" style={{ background: `${activity.color}15`, color: activity.color }}>
                      {activity.icon}
                    </div>
                    <div>
                      <h3>{activity.title}</h3>
                      <p className="activity-description">{activity.description}</p>
                    </div>
                  </Reveal>

                  <div className="activity-features">
                    {activity.features.map((feature, index) => (
                      <Reveal 
                        key={feature.name} 
                        className="feature-card" 
                        delay={index * 80}
                        style={{ borderLeftColor: activity.color }}
                      >
                        <div className="feature-icon" style={{ background: `${activity.color}15`, color: activity.color }}>
                          {feature.icon}
                        </div>
                        <div className="feature-content">
                          <h4>{feature.name}</h4>
                          <p>{feature.description}</p>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* --- Impact Section --- */}
          <section className="impact-section">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Our Impact</span>
              <h2>Transforming Lives Through Activities</h2>
              <div className="rule" />
            </Reveal>
            <div className="impact-grid">
              <Reveal className="impact-card" delay={0}>
                <div className="impact-number">500+</div>
                <div className="impact-label">Students trained in assistive technology</div>
              </Reveal>
              <Reveal className="impact-card" delay={80}>
                <div className="impact-number">50+</div>
                <div className="impact-label">Tactile science models developed</div>
              </Reveal>
              <Reveal className="impact-card" delay={160}>
                <div className="impact-number">1000+</div>
                <div className="impact-label">Braille books in library</div>
              </Reveal>
              <Reveal className="impact-card" delay={240}>
                <div className="impact-number">LIMCA</div>
                <div className="impact-label">Book of World Records for Braille Bhagavad Gita</div>
              </Reveal>
            </div>
          </section>

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-activities">
            <div className="cta-content">
              <span className="cta-eyebrow">Support Our Activities</span>
              <h2>Help us empower more lives through education and skill development.</h2>
              <p>Your support enables us to provide assistive technology, vocational training, and quality education to visually challenged individuals.</p>
              <div className="cta-buttons">
                <a className="btn btn-primary" href="donation.html">Donate Now</a>
                <a className="btn btn-outline btn-outline-light" href="volunteer-registration.html">Become a Volunteer</a>
              </div>
            </div>
          </Reveal>

        </main>
      </div>
      <Footer />
    </>
  );
}